import React, { useEffect, useState } from 'react';
// Use a div-based modal structure similar to CountryModal
import InputGroup from '../../components/FormElements/InputGroup';
import { CloseIcon } from '../../assets/Icons';
import { useDirectInputApi } from '../../store/api/hooks/directInputApiHook';
import { toast } from 'react-hot-toast';
import { set } from 'react-hook-form';
import LoadingSpinner from '../../components/LoadingSpinner';
import Button from '../../components/Buttons';

interface ProducerModalProps {
  isOpen: boolean;
  onClose: () => void;
  producerId: string;
  onUpdated?: () => void;
}

const ProducerModal: React.FC<ProducerModalProps> = ({ isOpen, onClose, producerId, onUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [literal, setLiteral] = useState('');
  const [producerName, setProducerName] = useState('');
  const [producerShow, setProducerShow] = useState('');
  const [keyword, setKeyword] = useState('');
  const { getProducerById, updateProducer } = useDirectInputApi();

  useEffect(() => {
    if (!isOpen || !producerId) return;
    fetchProducerById(producerId);
  }, [isOpen, producerId]);

  const handleRegenerateKeyword = () => {
      toast.error('Api Required')
  };

  const fetchProducerById = async (id: string) => {
    try {
      setLoading(true);
      const res = await getProducerById(id);
      if (res.data?.data) {
        setLiteral(res.data.data.literal || '');
        setProducerName(res.data.data.name || '');
        setProducerShow(res.data.data.name_show || '');
        setKeyword(res.data.data.producer_keyword.keyword || '');
      }
      toast.success(res.data?.message || 'Producer fetched successfully');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to fetch producer');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    setUpdating(true);
    setError('');
    try {
      await updateProducer(producerId, {
        name: producerName,
        name_show: producerShow,
        keyword,
      });
      if (onUpdated) onUpdated();
      onClose();
    } catch {
      setError('Failed to update producer');
    } finally {
      setUpdating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="app-modal-overlay">
      <div className="app-modal-container min-w-[500px]">
        <div className="app-modal-header">
          <h2 className="app-modal-title">Producer: {literal || producerId}</h2>
          <button
            onClick={onClose}
            className="app-modal-close-btn"
          >
            <CloseIcon className='w-4 h-4' />
          </button>
        </div>
        <div className="app-modal-body">
          {error && toast.error(error)}
          {loading ? (
            <div className="flex justify-center items-center min-h-[150px]">
              <LoadingSpinner className="w-6 h-6" />
            </div>
          ) : (
            
            <form onSubmit={e => { e.preventDefault(); handleUpdate(); }}>
              <InputGroup
                type="text"
                label="Producer Name"
                name="producerName"
                value={producerName}
                handleChange={e => setProducerName(e.target.value)}
                inputVariant="floating"
                className="mb-3"
              />
              <InputGroup
                type="text"
                label="Producer Show"
                name="producerShow"
                value={producerShow}
                handleChange={e => setProducerShow(e.target.value)}
                inputVariant="floating"
                className="mb-3"
              />
              <div className='grid grid-cols-12 gap-2'>
                <div className='col-span-8'>
                  <InputGroup
                    type="text"
                    label="Keyword"
                    name="keyword"
                    value={keyword}
                    handleChange={e => setKeyword(e.target.value)}
                    inputVariant="floating"
                    className="mb-3"
                  />
                </div>
                <div className='col-span-4'>
                  <Button
                    type="button"
                    size='lg'
                    variant="primary"
                    onClick={handleRegenerateKeyword}
                    className="whitespace-nowrap"
                  >
                    Re-Generate
                  </Button>
                </div>
              </div>
              <div className="app-modal-footer flex gap-2 mt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="app-modal-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updating}
                  className="app-modal-submit-btn"
                >
                  {updating ? 'Updating...' : 'Update'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProducerModal;
